import React, { useState, useEffect, useMemo, useRef } from "react";
import { Plus, X, FileText } from "lucide-react";
import ExportCard from "./ExportCard";
import ExportForm from "./ExportForm";
import SendBoardDiscrepancyModal from "./SendBoardDiscrepancyModal";
import { ExportAndSendService } from "../../services/exportAndSendService";
import { supabase } from "../../lib/supabase";
import { Button, SearchBar, Modal, ModalBody } from "../ui";
import "./SendTab.css";

const SendTab = ({ projects, onUpdateProject, clients, employees, exportId }) => {
  const [exports, setExports] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [selectedExport, setSelectedExport] = useState(null);
  const [showExportForm, setShowExportForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAllStatuses, setShowAllStatuses] = useState(false); // Default to false (drafts only)
  const [showCreateExportModal, setShowCreateExportModal] = useState(false);
  const [createExportSearch, setCreateExportSearch] = useState("");
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectContact, setProjectContact] = useState(null);
  const [clientContacts, setClientContacts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
  const [discrepancyModalProject, setDiscrepancyModalProject] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const createExportSearchRef = useRef(null);
  const createExportRef = useRef(null);

  useEffect(() => {
    loadAllExports();
    loadCurrentUser();
    loadAllProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus search input when create export modal opens
  useEffect(() => {
    if (showCreateExportModal && createExportSearchRef.current) {
      setTimeout(() => {
        createExportSearchRef.current?.focus();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateExportModal]);

  // Auto-select export when exportId is provided in URL
  useEffect(() => {
    if (exportId && exports.length > 0) {
      const targetExport = exports.find(exp => exp.id === exportId);
      if (targetExport) {
        // Use handleExportCardClick to load full data
        handleExportCardClick(targetExport);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportId, exports]);


  const loadCurrentUser = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user && employees) {
        const employee = employees.find((emp) => emp.email === user.email);
        setCurrentUser(employee);
      }
    } catch (error) {
      console.error("Error loading current user:", error);
    }
  };

  const loadAllProjects = async () => {
    try {
      setLoadingProjects(true);
      const { data, error } = await supabase
        .from("projects")
        .select(
          `
          id,
          name,
          job_number,
          client_id,
          contact_id,
          file_formats_needed,
          modeling_status,
          modeling_state,
          initial_acceptance,
          clients (
            id,
            name,
            brand
          ),
          contacts (
            id,
            name,
            email
          )
        `,
        )
        .order("job_number", { ascending: false });

      if (error) throw error;

      // Transform the data to match expected format
      const transformedProjects = (data || []).map((project) => ({
        ...project,
        projectName: project.name,
        jobNumber: project.job_number,
        clientName: project.clients?.name || "No Client",
        contactName: project.contacts?.name || "No Contact",
        fileFormatsNeeded: project.file_formats_needed || [],
      }));

      // Sort projects with "Ready to Export" status at the top, then by initial acceptance date
      const sortedProjects = transformedProjects.sort((a, b) => {
        // Check if projects have "Ready to Export" status
        const aReadyToExport = a.modeling_status === "Ready to Export";
        const bReadyToExport = b.modeling_status === "Ready to Export";

        // If one is Ready to Export and the other isn't, prioritize the Ready to Export one
        if (aReadyToExport && !bReadyToExport) return -1;
        if (!aReadyToExport && bReadyToExport) return 1;

        // If both have same Ready to Export status, sort by initial acceptance date (most recent first)
        const dateA = a.initial_acceptance ? new Date(a.initial_acceptance).getTime() : 0;
        const dateB = b.initial_acceptance ? new Date(b.initial_acceptance).getTime() : 0;
        return dateB - dateA; // Descending order (most recent first)
      });

      setAllProjects(sortedProjects);
    } catch (error) {
      console.error("Error loading all projects:", error);
      setAllProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadAllExports = async () => {
    try {
      setLoading(true);
      // Fetch card display data + project info needed for ExportForm
      // Full export data (files, emails) is loaded when opening ExportForm
      const { data, error } = await supabase
        .from("export_and_send")
        .select(
          `
          id,
          project_id,
          status,
          created_at,
          sent_date,
          export_date,
          related_discrepancy_ids,
          file_formats_needed,
          projects!project_id (
            id,
            name,
            job_number,
            contact_id,
            client_id,
            model_request_additional_info,
            additional_requests,
            file_formats_needed,
            clients!client_id (
              id,
              name,
              brand,
              trimble_formats
            ),
            contacts!contact_id (
              id,
              name,
              email,
              phone_number,
              client_id,
              clients!client_id (
                id,
                name
              )
            )
          )
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform the data to include project info in each export
      const exportsWithProjectInfo = (data || []).map((exp) => ({
        ...exp,
        projectName: exp.projects?.name || "Unknown Project",
        jobNumber: exp.projects?.job_number || "N/A",
        clientName: exp.projects?.clients?.name || "N/A",
        projectId: exp.project_id,
        project: exp.projects,
      }));

      setExports(exportsWithProjectInfo);
    } catch (error) {
      console.error("Error loading exports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCardClick = async (exportItem) => {
    try {
      // Load FULL export data when opening (not loaded in list view for performance)
      const { data: fullExport, error } = await supabase
        .from("export_and_send")
        .select("*")
        .eq("id", exportItem.id)
        .single();

      if (error) throw error;

      // Merge with project info from the card
      const fullExportWithProject = {
        ...fullExport,
        project: exportItem.project,
        projectName: exportItem.projectName,
        jobNumber: exportItem.jobNumber,
        clientName: exportItem.clientName,
      };

      setSelectedExport(fullExportWithProject);

      // Load project details for the selected export
      if (exportItem.project) {
        await loadProjectDetails(exportItem.project);
      }

      setShowExportForm(true);
    } catch (error) {
      console.error("Error loading full export data:", error);
      alert("Failed to load export details. Please try again.");
    }
  };

  const loadProjectDetails = async (project) => {
    try {
      // Use already-loaded contact from project join if available
      if (project.contacts) {
        // Enhance contact with clientName from nested clients object
        const enhancedContact = {
          ...project.contacts,
          clientName: project.contacts.clients?.name || null,
        };
        setProjectContact(enhancedContact);
      } else if (project.contact_id) {
        // Fallback: load contact if not in project data
        const { data: contactData } = await supabase
          .from("contacts")
          .select(`
            *,
            clients!client_id (
              id,
              name
            )
          `)
          .eq("id", project.contact_id)
          .single();

        if (contactData) {
          // Enhance contact with clientName
          const enhancedContact = {
            ...contactData,
            clientName: contactData.clients?.name || null,
          };
          setProjectContact(enhancedContact);
        }
      }

      if (project.client_id) {
        const { data: contacts } = await supabase
          .from("contacts")
          .select("*")
          .eq("client_id", project.client_id)
          .order("name");

        if (contacts) {
          setClientContacts(contacts);
        }
      }
    } catch (error) {
      console.error("Error loading project details:", error);
    }
  };

  const handleCloseForm = () => {
    setShowExportForm(false);
    setSelectedExport(null);
    setProjectContact(null);
    // Keep clientContacts cached to preserve email recipients when reopening
    // setClientContacts([]);
  };

  const handleCreateExport = () => {
    setShowCreateExportModal(true);
  };

  const handleCloseCreateExportModal = () => {
    setShowCreateExportModal(false);
    setCreateExportSearch("");
    setShowProjectDropdown(false);
  };

  const handleSelectProjectForExport = async (project) => {
    try {
      // Create new export for the selected project
      const newExport = await ExportAndSendService.createExport({
        project_id: project.id,
        status: "draft",
        file_formats_needed:
          project.fileFormatsNeeded || project.file_formats_needed || [],
        additional_contact_ids: [],
        email_body: "",
        topcon_files: [],
        leica_files: [],
        vcl_files: [],
        dxf_ttm_files: [],
        svd_svl_files: [],
        background_image_files: [],
        created_by: currentUser?.id,
      });

      // Close the create export modal
      handleCloseCreateExportModal();

      // Refresh exports list
      await loadAllExports();

      // Open the newly created export
      const exportWithProjectInfo = {
        ...newExport,
        project: project,
        projectName: project.projectName || project.name,
        jobNumber: project.jobNumber || project.job_number,
        clientName: project.clientName,
      };
      handleExportCardClick(exportWithProjectInfo);
    } catch (error) {
      console.error("Error creating export for project:", error);
      alert("Failed to create export. Please try again.");
    }
  };

  const handleCreateDiscrepancy = (project) => {
    setDiscrepancyModalProject(project);
    setShowDiscrepancyModal(true);
  };

  const handleCloseDiscrepancyModal = () => {
    setShowDiscrepancyModal(false);
    setDiscrepancyModalProject(null);
  };

  const handleDiscrepancyCreated = async (discrepancy) => {
    try {
      // Auto-attach the new discrepancy to the current export
      if (selectedExport && discrepancy) {
        const currentRelatedIds = selectedExport.related_discrepancy_ids || [];
        const updatedRelatedIds = [...currentRelatedIds, discrepancy.id];
        
        await ExportAndSendService.updateExport(selectedExport.id, {
          related_discrepancy_ids: updatedRelatedIds,
        });
        
        // Update the local selectedExport state
        const updatedExport = {
          ...selectedExport,
          related_discrepancy_ids: updatedRelatedIds,
        };
        setSelectedExport(updatedExport);
        
        // Trigger refresh in ExportForm to reload discrepancies list
        setRefreshTrigger(prev => prev + 1);
        
        // Refresh exports list to show the updated export
        await loadAllExports();
        
        console.log(`Auto-attached discrepancy ${discrepancy.id} to export ${selectedExport.id}`);
      }
    } catch (error) {
      console.error("Error auto-attaching discrepancy to export:", error);
    }
  };

  const filteredExports = useMemo(() => {
    let filtered = exports;

    // Filter by search term (always searches ALL exports)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((exp) => {
        return (
          exp.projectName?.toLowerCase().includes(searchLower) ||
          exp.jobNumber?.toLowerCase().includes(searchLower) ||
          exp.clientName?.toLowerCase().includes(searchLower)
        );
      });
      // When searching, show all results regardless of status
      return filtered;
    }

    // Filter by status (drafts only by default when NOT searching)
    if (!showAllStatuses) {
      filtered = filtered.filter((exp) => exp.status === "draft");
    }

    return filtered;
  }, [exports, searchTerm, showAllStatuses]);

  // Group exports by status when showing all or when searching
  const groupedExports = useMemo(() => {
    // If not showing all and not searching, just show drafts
    if (!showAllStatuses && !searchTerm) {
      return { draft: filteredExports };
    }

    // Group by status when showing all OR when searching
    const groups = {
      draft: [],
      pending: [],
      sent: [],
      other: [],
    };

    filteredExports.forEach((exp) => {
      const status = exp.status?.toLowerCase() || 'other';
      if (groups[status]) {
        groups[status].push(exp);
      } else {
        groups.other.push(exp);
      }
    });

    // Return only non-empty groups in order: draft, pending, sent, other
    const orderedGroups = {};
    ['draft', 'pending', 'sent', 'other'].forEach(key => {
      if (groups[key].length > 0) {
        orderedGroups[key] = groups[key];
      }
    });

    return orderedGroups;
  }, [filteredExports, showAllStatuses, searchTerm]);

  const createExportFilteredProjects = useMemo(() => {
    const projectsToFilter = allProjects || [];

    if (!createExportSearch) return projectsToFilter;

    const searchLower = createExportSearch.toLowerCase();
    const filteredProjects = projectsToFilter.filter((project) => {
      const projectName = (
        project.projectName ||
        project.name ||
        ""
      ).toLowerCase();
      const jobNumber = (
        project.jobNumber ||
        project.job_number ||
        ""
      ).toLowerCase();
      const clientName = (project.clientName || "").toLowerCase();

      return (
        projectName.includes(searchLower) ||
        jobNumber.includes(searchLower) ||
        clientName.includes(searchLower)
      );
    });

    // Apply the same sorting logic: Ready to Export first, then by initial acceptance date
    return filteredProjects.sort((a, b) => {
      // Check if projects have "Ready to Export" status
      const aReadyToExport = a.modeling_status === "Ready to Export";
      const bReadyToExport = b.modeling_status === "Ready to Export";

      // If one is Ready to Export and the other isn't, prioritize the Ready to Export one
      if (aReadyToExport && !bReadyToExport) return -1;
      if (!aReadyToExport && bReadyToExport) return 1;

      // If both have same Ready to Export status, sort by initial acceptance date (most recent first)
      const dateA = a.initial_acceptance ? new Date(a.initial_acceptance).getTime() : 0;
      const dateB = b.initial_acceptance ? new Date(b.initial_acceptance).getTime() : 0;
      return dateB - dateA; // Descending order (most recent first)
    });
  }, [allProjects, createExportSearch]);

  // Handle click outside dropdown for create export modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        createExportRef.current &&
        !createExportRef.current.contains(event.target)
      ) {
        setShowProjectDropdown(false);
      }
    };

    if (showProjectDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showProjectDropdown]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--space-6) var(--space-6) var(--space-4)',
          backgroundColor: 'var(--dark-bg)',
          borderBottom: '1px solid var(--gray-600)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            Exports
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}
          >
            {filteredExports.length} {searchTerm ? '' : (showAllStatuses ? '' : 'draft ')} export{filteredExports.length !== 1 ? 's' : ''}
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={handleCreateExport}
        >
          + New Export
        </Button>
      </div>

      {/* Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-4)',
        backgroundColor: 'var(--dark-bg)',
        borderBottom: '1px solid var(--gray-600)',
        gap: 'var(--space-4)',
        flexWrap: 'wrap',
      }}>
        <div style={{ width: '300px', flexShrink: 0 }}>
          <SearchBar
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search exports..."
            size="sm"
          />
        </div>

        <Button
          variant={showAllStatuses ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setShowAllStatuses(!showAllStatuses)}
        >
          {showAllStatuses ? '✓ ' : ''}Show All
        </Button>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)' }}>
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px',
            fontSize: 'var(--text-base)',
            color: 'var(--gray-400)'
          }}>
            Loading exports...
          </div>
        ) : filteredExports.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--space-12)',
              color: 'var(--gray-400)',
            }}
          >
            <FileText size={48} style={{ marginBottom: 'var(--space-4)' }} />
            <p style={{
              fontSize: 'var(--text-lg)',
              margin: 0,
              fontWeight: 'var(--font-medium)'
            }}>
              {searchTerm || !showAllStatuses
                ? "No exports found matching your filters"
                : "No exports found"}
            </p>
          </div>
        ) : (
          <div>
            {Object.entries(groupedExports).map(([status, exportsInGroup]) => (
              <div key={status} style={{ marginBottom: (showAllStatuses || searchTerm) ? 'var(--space-8)' : 0 }}>
                {(showAllStatuses || searchTerm) && (
                  <h2
                    style={{
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-300)',
                      marginBottom: 'var(--space-4)',
                      textTransform: 'capitalize',
                    }}
                  >
                    {status} ({exportsInGroup.length})
                  </h2>
                )}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 'var(--space-5)',
                  }}
                >
                  {exportsInGroup.map((exportItem) => (
                    <ExportCard
                      key={exportItem.id}
                      export={exportItem}
                      onClick={() => handleExportCardClick(exportItem)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showExportForm && selectedExport && (
        <div className="export-modal-backdrop" onClick={(e) => e.target === e.currentTarget && handleCloseForm()}>
          <div className="export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="export-modal-header">
              <h2>Export - {selectedExport.projectName}</h2>
              <button className="export-modal-close" onClick={handleCloseForm}>
                <X size={20} />
              </button>
            </div>
            <div className="export-modal-body">
              <ExportForm
                export={selectedExport}
                project={selectedExport.project}
                projectContact={projectContact}
                clientContacts={clientContacts}
                currentUser={currentUser}
                refreshTrigger={refreshTrigger}
                onSave={async (exportId, updates) => {
                  // Always update database to ensure all email data is persisted
                  const updatedExport = await ExportAndSendService.updateExport(
                    exportId,
                    updates,
                  );
                  setExports(prevExports =>
                    prevExports.map(exp =>
                      exp.id === exportId ? { ...exp, ...updatedExport} : exp
                    )
                  );
                  return updatedExport;
                }}
                onBack={handleCloseForm}
                onSuccess={async () => {
                  // Optimistically remove the sent card from the board immediately
                  if (selectedExport?.id) {
                    setExports(prev => prev.filter(exp => exp.id !== selectedExport.id));
                  }
                  // Close the modal immediately
                  handleCloseForm();
                  // Then refresh the full list in the background
                  await loadAllExports();
                }}
                isCreating={false}
                onCreateDiscrepancy={handleCreateDiscrepancy}
                onDelete={async (exportId) => {
                  await ExportAndSendService.deleteExport(exportId);
                  await loadAllExports();
                  handleCloseForm();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Create Export Modal */}
      {showCreateExportModal && (
        <Modal trackAs="CreateExportModal" isOpen={showCreateExportModal} onClose={handleCloseCreateExportModal} size="lg">
          {/* Custom Header with Icon */}
          <div
            style={{
              padding: 'var(--space-6)',
              borderBottom: '1px solid var(--gray-700)',
              background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
              borderTopLeftRadius: 'var(--radius-xl)',
              borderTopRightRadius: 'var(--radius-xl)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: 'var(--radius-full)',
                  backgroundColor: 'var(--cta-orange)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Plus size={28} color="white" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-semibold)',
                    color: 'var(--gray-50)',
                  }}
                >
                  Create Export
                </h1>
                <p
                  style={{
                    margin: 0,
                    marginTop: 'var(--space-1)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--gray-400)',
                  }}
                >
                  Select a project to create a new export package
                </p>
              </div>
            </div>
          </div>

          <ModalBody style={{ display: 'flex', flexDirection: 'column' }}>
            <div ref={createExportRef} style={{ display: 'flex', flexDirection: 'column' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                }}
              >
                Select Project
              </label>

              {/* Search Input */}
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <SearchBar
                  ref={createExportSearchRef}
                  value={createExportSearch}
                  onChange={(e) => {
                    setCreateExportSearch(e.target.value);
                    setShowProjectDropdown(true);
                  }}
                  onFocus={() => setShowProjectDropdown(true)}
                  placeholder="Search by project name, job number, or client..."
                  size="md"
                />
              </div>

              {/* Project Dropdown - Fixed height */}
              <div
                style={{
                  height: '400px',
                  minHeight: '400px',
                  maxHeight: '400px',
                  background: 'var(--dark-surface)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-lg)',
                  overflowY: 'auto',
                  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
                }}
              >
                {loadingProjects ? (
                  <div
                    style={{
                      padding: 'var(--space-6)',
                      textAlign: 'center',
                      color: 'var(--gray-400)',
                      fontSize: 'var(--text-sm)',
                    }}
                  >
                    Loading projects...
                  </div>
                ) : createExportFilteredProjects.length === 0 ? (
                  <div
                    style={{
                      padding: 'var(--space-6)',
                      textAlign: 'center',
                      color: 'var(--gray-400)',
                      fontSize: 'var(--text-sm)',
                      fontStyle: 'italic',
                    }}
                  >
                    {createExportSearch
                      ? "No projects found"
                      : "No projects available"}
                  </div>
                ) : (
                  <div>
                    {createExportFilteredProjects.map((project) => (
                      <div
                        key={project.id}
                        onClick={() => handleSelectProjectForExport(project)}
                        style={{
                          padding: 'var(--space-4)',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--gray-700)',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--gray-700)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-1)',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--gray-400)',
                            }}
                          >
                            {project.jobNumber || project.job_number}
                          </span>
                          <span
                            style={{
                              fontSize: 'var(--text-base)',
                              fontWeight: 'var(--font-medium)',
                              color: 'var(--gray-50)',
                            }}
                          >
                            {project.projectName ||
                              project.name ||
                              "Unnamed Project"}
                          </span>
                        </div>
                        {project.clientName && (
                          <div
                            style={{
                              fontSize: 'var(--text-sm)',
                              color: 'var(--gray-400)',
                            }}
                          >
                            {project.clientName}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ModalBody>
        </Modal>
      )}

      {/* Discrepancy Modal */}
      <SendBoardDiscrepancyModal
        isOpen={showDiscrepancyModal}
        onClose={handleCloseDiscrepancyModal}
        onSave={handleDiscrepancyCreated}
        projects={allProjects}
        clients={clients}
        employees={employees}
        selectedProject={discrepancyModalProject}
      />
    </div>
  );
};

export default SendTab;
